import React, { useEffect } from 'react';
import {
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
} from 'react-router-dom';
import { RootLayoutWithTopHeader } from 'routes/layouts/RootLayoutWithTopHeader';
import { LayoutWithNavigation } from 'routes/layouts/LayoutWithNavigation';
import { HomePage } from 'routes/home';
import { NotFound } from 'routes/notFound';
import { sentryCreateBrowserRouter } from 'utils/Sentry';
import { TravelVoucherform } from 'routes/travelVouchers/form/TravelVoucherForm';
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from '@azure/msal-react';

import { AuthProvider, useAuth, UserRoles } from 'components/Auth/AuthProvider';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { OverviewTable } from 'routes/travelRequestsOverview/OverviewTable/OverviewTable';
import { OverviewByApprover } from 'routes/travelRequestsOverview/OverviewByApprover/OverviewByApprover';
import { BreadcrumbLink } from '@chakra-ui/react';
import { NavLink } from 'react-router-dom';
import { PreviewTabs } from 'routes/previews/PreviewTabs';
import { useDispatch } from 'react-redux';
import { ApproversOverview } from 'routes/admin/AdminApproversOverview/ApproversOverviewTable/ApproversOverview';
import { CreateOrUpdateApprover } from 'routes/admin/AdminApproversOverview/ApproverForm/CreateOrUpdateApprover';
import { ActivityLog } from 'routes/activityLog/ActivityLog';
import { NoAccess } from 'routes/noAccess';
import { AdminPage } from 'routes/admin';
import { Spinner } from 'components/Spinner';
import { useUserQuery } from 'api/services/User';
import { useApproversListQuery } from 'api/services/Admin';
import { setUser } from 'api/slices/userSlice';
import { setApprovers } from 'api/slices/approversSlice';
import { isUserApprover } from 'utils/helpers';
import { SignIn } from 'routes/signIn';
import { ErrorPage } from 'routes/errorPage';
import { AdminRequestsOverview } from 'routes/admin/AdminRequestsOverview/AdminRequestsOverview';
import { AdminNotificationsOverview } from 'routes/admin/AdminNotificationsRequestsOverview/AdminNotificationsOverview';

export const RestrictedRoute = ({
  conditional,
  role,
  children,
}: {
  children: JSX.Element;
  conditional?: boolean | null;
  role?: UserRoles | null;
}): JSX.Element => {
  const { user, hasRoles, hasAccess } = useAuth();

  if (!user) return <></>;

  if (conditional && hasAccess()) {
    return children;
  }
  if (role && hasRoles(role)) {
    return children;
  }

  return <Navigate to="/" />;
};

const ProtectedRoute = ({
  children,
}: {
  children?: JSX.Element;
}): JSX.Element => {
  const { user, isAuthenticated } = useAuth();

  if (isAuthenticated && !user) {
    return <Spinner />;
  }

  return children ? children : <Outlet />;
};

const router = sentryCreateBrowserRouter(
  createRoutesFromElements(
    <Route element={<AuthProvider />}>
      <Route element={<ProtectedRoute />}>
        <Route element={<RootLayoutWithTopHeader />}>
          <Route>
            <Route path="/" element={<LayoutWithNavigation />}>
              <Route
                path="samthykktir"
                element={
                  <RestrictedRoute conditional>
                    <OverviewByApprover />
                  </RestrictedRoute>
                }
                handle={{
                  crumb: (): JSX.Element => (
                    <BreadcrumbLink as={NavLink} to="/samthykktir">
                      Samþykktir
                    </BreadcrumbLink>
                  ),
                }}
              />
              <Route
                path="ferdabeidnir"
                errorElement={<ErrorPage />}
                handle={{
                  crumb: (): JSX.Element => (
                    <BreadcrumbLink as={NavLink} to="/ferdabeidnir">
                      Ferðabeiðnir
                    </BreadcrumbLink>
                  ),
                }}
              >
                <Route index element={<OverviewTable />} />
                <Route
                  path="/ferdabeidnir/ferdabeidni"
                  element={<TravelVoucherform />}
                  handle={{
                    crumb: (_params, search: string): JSX.Element => {
                      const id = new URLSearchParams(search).get('id');
                      return (
                        <BreadcrumbLink
                          as={NavLink}
                          to={{
                            pathname: '/ferdabeidnir/ferdabeidni',
                            search: `?id=${id}`,
                          }}
                        >
                          Beiðni {id}
                        </BreadcrumbLink>
                      );
                    },
                  }}
                />
                <Route
                  path="/ferdabeidnir/ferdabeidni/preview"
                  element={<PreviewTabs />}
                  handle={{
                    crumb: (_params, search): JSX.Element => {
                      const id = new URLSearchParams(search).get('id');
                      const action = new URLSearchParams(search).get('action');
                      return (
                        <BreadcrumbLink
                          as={NavLink}
                          to={{
                            pathname: '/ferdabeidnir/ferdabeidni/preview',
                            search: `?id=${id}&action=${action}`,
                          }}
                        >
                          Beiðni {id}
                        </BreadcrumbLink>
                      );
                    },
                  }}
                />
                <Route
                  path="/ferdabeidnir/ferdabeidni/preview"
                  element={<PreviewTabs />}
                  handle={{
                    crumb: (_params, search): JSX.Element => {
                      const id = new URLSearchParams(search).get('id');
                      const action = new URLSearchParams(search).get('action');
                      const itemId = new URLSearchParams(search).get('itemId');
                      return (
                        <BreadcrumbLink
                          as={NavLink}
                          to={{
                            pathname: '/ferdabeidnir/ferdabeidni/preview',
                            search: `?id=${id}&action=${action}?itemId=${itemId}`,
                          }}
                        >
                          Beiðni {id}
                        </BreadcrumbLink>
                      );
                    },
                  }}
                />
              </Route>
              <Route index element={<HomePage />} />
              <Route
                path="/admin"
                handle={{
                  crumb: (): JSX.Element => (
                    <BreadcrumbLink as={NavLink} to="/admin/ferdabeidnir">
                      Umsjón
                    </BreadcrumbLink>
                  ),
                }}
              >
                <Route
                  index
                  element={
                    <RestrictedRoute role={'travel-request-view'}>
                      <AdminPage />
                    </RestrictedRoute>
                  }
                />
                <Route
                  path="/admin/ferdabeidnir"
                  handle={{
                    crumb: (): JSX.Element => {
                      return (
                        <BreadcrumbLink
                          as={NavLink}
                          to={{
                            pathname: '/admin/ferdabeidnir',
                          }}
                        >
                          Umsjón beiðna
                        </BreadcrumbLink>
                      );
                    },
                  }}
                >
                  <Route
                    index
                    element={
                      <RestrictedRoute role={'travel-request-view'}>
                        <AdminRequestsOverview />
                      </RestrictedRoute>
                    }
                  />
                  <Route
                    path="/admin/ferdabeidnir/ferdabeidni/preview"
                    element={<PreviewTabs />}
                    handle={{
                      crumb: (_params, search): JSX.Element => {
                        const id = new URLSearchParams(search).get('id');
                        const action = new URLSearchParams(search).get(
                          'action'
                        );
                        return (
                          <BreadcrumbLink
                            as={NavLink}
                            to={{
                              pathname:
                                '/admin/ferdabeidnir/ferdabeidni/preview',
                              search: `?id=${id}&action=${action}`,
                            }}
                          >
                            Beiðni {id}
                          </BreadcrumbLink>
                        );
                      },
                    }}
                  />
                  <Route
                    path="/admin/ferdabeidnir/ferdabeidni/preview"
                    element={<PreviewTabs />}
                    handle={{
                      crumb: (_params, search): JSX.Element => {
                        const id = new URLSearchParams(search).get('id');
                        const action = new URLSearchParams(search).get(
                          'action'
                        );
                        const itemId = new URLSearchParams(search).get(
                          'itemId'
                        );
                        return (
                          <BreadcrumbLink
                            as={NavLink}
                            to={{
                              pathname:
                                '/admin/ferdabeidnir/ferdabeidni/preview',
                              search: `?id=${id}&action=${action}?itemId=${itemId}`,
                            }}
                          >
                            Beiðni {id}
                          </BreadcrumbLink>
                        );
                      },
                    }}
                  />
                  <Route
                    path="/admin/ferdabeidnir/adgerdarsaga"
                    element={<ActivityLog />}
                    handle={{
                      crumb: (_params, search): JSX.Element => {
                        const id = new URLSearchParams(search).get('id');
                        return (
                          <BreadcrumbLink
                            as={NavLink}
                            to={{
                              pathname: '/admin/ferdabeidnir',
                              search: `?id=${id}`,
                            }}
                          >
                            Aðgerðarsaga - {id}
                          </BreadcrumbLink>
                        );
                      },
                    }}
                  />
                </Route>
                <Route
                  path="/admin/samthykkjendur"
                  handle={{
                    crumb: (): JSX.Element => (
                      <BreadcrumbLink as={NavLink} to="/admin/samthykkjendur">
                        Samþykktir
                      </BreadcrumbLink>
                    ),
                  }}
                >
                  <Route
                    index
                    element={
                      <RestrictedRoute role={'travel-request-admin'}>
                        <ApproversOverview />
                      </RestrictedRoute>
                    }
                  />
                  <Route
                    path="/admin/samthykkjendur/action"
                    element={<CreateOrUpdateApprover />}
                    errorElement={<ErrorPage />}
                    handle={{
                      crumb: (_params, search): JSX.Element => {
                        const mode = new URLSearchParams(search).get('mode');
                        return (
                          <BreadcrumbLink
                            as={NavLink}
                            to={{
                              pathname: '/admin/samthykkjendur/action',
                              search: `?mode=${mode}`,
                            }}
                          >
                            {mode === 'create'
                              ? 'Stofna samþykkjanda'
                              : 'Breyta samþykkjanda'}
                          </BreadcrumbLink>
                        );
                      },
                    }}
                  />
                </Route>
                <Route
                  path="/admin/email"
                  handle={{
                    crumb: (): JSX.Element => (
                      <BreadcrumbLink as={NavLink} to="/admin/email">
                        Tilkynningapóstar
                      </BreadcrumbLink>
                    ),
                  }}
                >
                  <Route
                    index
                    element={
                      <RestrictedRoute role={'travel-request-view'}>
                        <AdminNotificationsOverview />
                      </RestrictedRoute>
                    }
                  />
                </Route>
              </Route>
            </Route>
            <Route path="*" element={<NotFound />} />
            <Route path="/404" element={<NotFound />} />
            <Route path="/no-access" element={<NoAccess />} />
          </Route>
        </Route>
      </Route>
    </Route>
  )
);

export const App: React.FC = () => {
  const dispatch = useDispatch();
  const { data: userInfo } = useUserQuery();
  const { data: approvers } = useApproversListQuery();
  console.log('UserInfo:', userInfo);
  useEffect(() => {
    if (approvers) dispatch(setApprovers(approvers));
    if (userInfo && approvers) {
      const isApprover = isUserApprover(approvers, userInfo.data[0].adUser);
      const userObject = {
        ...userInfo.data[0],
        hasAccess: isApprover,
      };
      dispatch(setUser(userObject));
    }
  }, [userInfo, approvers, dispatch]);

  return (
    <>
      <UnauthenticatedTemplate>
        <SignIn />
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>
        <RouterProvider router={router} />
      </AuthenticatedTemplate>
      {/* {process.env.NODE_ENV === 'development' && (
      <ReactQueryDevtools position="bottom-right" />
    )} */}
      <ToastContainer
        position="bottom-left"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnHover
        theme="light"
      />
    </>
  );
};
